import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProfileUpdate } from './useProfileUpdate';
import { mapAnswersToPreferences } from '../components/discovery/quizData';
import type { QuizAnswers } from '../components/discovery/quizTypes';

const STORAGE_KEY = 'discovery_quiz_answers';
const REDIRECT_KEY = 'discovery_quiz_redirect';
const WELCOME_KEY = 'discovery_quiz_welcome';

/**
 * Watches for an authenticated user + pending quiz answers in localStorage.
 * When both exist, persists preferences to Firestore via the existing
 * update_profile_callable, removes the local cache, and stores welcome
 * data for the PostLoginWelcome explainer modal to handle navigation.
 */
export const useDiscoveryQuizSaver = () => {
  const { user, userAccount } = useAuth();
  const { updateProfile } = useProfileUpdate();
  const processedRef = useRef(false);

  useEffect(() => {
    if (!user) {
      processedRef.current = false;
      return;
    }

    // Prevent double-processing (userAccount loads async and triggers re-run)
    if (processedRef.current) return;

    const raw = localStorage.getItem(STORAGE_KEY);
    const redirect = localStorage.getItem(REDIRECT_KEY);

    // No pending quiz data to process
    if (!raw && !redirect) return;

    // Mark as processed to prevent re-runs
    processedRef.current = true;

    try {
      // Always set up welcome data first (for post-login navigation)
      // This ensures user is guided to tool even if profile update fails
      if (redirect) {
        const answers: QuizAnswers = raw ? JSON.parse(raw) : {};
        localStorage.setItem(
          WELCOME_KEY,
          JSON.stringify({
            route: redirect,
            persona: answers.persona ?? 'builder',
            goal: answers.goal ?? 'balanced',
            risk: answers.risk ?? 'moderate',
            horizon: answers.horizon ?? 'medium',
            needs: answers.needs ?? [],
          }),
        );
        localStorage.removeItem(REDIRECT_KEY);
      }

      // Only update profile if we have quiz answers AND haven't already done it
      if (raw && !userAccount?.onboardingSurvey?.completed) {
        const answers: QuizAnswers = JSON.parse(raw);
        const { preferences, onboardingSurvey } = mapAnswersToPreferences(answers);

        // Fire-and-forget — errors are logged inside useProfileUpdate
        updateProfile({
          preferences,
          onboardingComplete: true,
        } as any).then(() => {
          // Second call for onboardingSurvey since the type is strict
          updateProfile({ onboardingSurvey } as any);
        });
      }

      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Corrupted data — clear it
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(REDIRECT_KEY);
    }
  }, [user, userAccount, updateProfile]);
};
